/** Shared, dependency-free contract for marketing assignment and app attribution. */
export const LANDING_EXPERIMENT = 'account-first-v1';
export const LANDING_EXPERIMENT_HEADER = 'x-homepoint-landing-experiment';
export const LANDING_EXPERIMENT_DAYS = 30;
// Attribution cookies are issued by the server and later read on device clocks.
const CLOCK_SKEW_MS = 5 * 60 * 1000;
export type LandingAudience = 'buyer' | 'seller';
export type LandingVariant = 'control' | 'account-first';
export interface LandingAssignment {
  experiment: typeof LANDING_EXPERIMENT;
  audience: LandingAudience;
  variant: LandingVariant;
  id: string;
  assignedAt: number;
  seenAt: number;
}
export const assignmentCookie = (audience: LandingAudience) => `hp_account_first_v1_${audience}`;
export function parseAssignment(value: string | undefined, now = Date.now()): LandingAssignment | null {
  if (!value || value.length > 1000) return null;
  try {
    const a = JSON.parse(decodeURIComponent(value));
    if (a.experiment !== LANDING_EXPERIMENT || !['buyer', 'seller'].includes(a.audience) ||
      !['control', 'account-first'].includes(a.variant) || typeof a.id !== 'string' ||
      !/^[a-f0-9-]{36}$/.test(a.id) || !Number.isFinite(a.assignedAt) || !Number.isFinite(a.seenAt) ||
      a.assignedAt > now + CLOCK_SKEW_MS || a.seenAt < a.assignedAt || a.seenAt > now + CLOCK_SKEW_MS ||
      now - a.assignedAt > LANDING_EXPERIMENT_DAYS * 86400000) return null;
    return { experiment: LANDING_EXPERIMENT, audience: a.audience, variant: a.variant,
      id: a.id, assignedAt: a.assignedAt, seenAt: a.seenAt };
  } catch { return null; }
}
export function assignmentFromCookies(cookie: string, now = Date.now()): LandingAssignment | null {
  const values = cookie.split(';').map(part => part.trim());
  return (['buyer', 'seller'] as const).map(audience => {
    const prefix = assignmentCookie(audience) + '=';
    const match = values.find(part => part.startsWith(prefix));
    const parsed = parseAssignment(match?.slice(prefix.length), now);
    return parsed?.audience === audience ? parsed : null;
  }).filter((a): a is LandingAssignment => a !== null).sort((a, b) => b.seenAt - a.seenAt)[0] ?? null;
}
export function assignmentProperties(a: LandingAssignment) {
  return { landing_experiment: a.experiment, landing_variant: a.variant,
    landing_audience: a.audience, landing_assignment_id: a.id };
}
export function newAssignment(audience: LandingAudience, id: string, random: number, now = Date.now()): LandingAssignment {
  return { experiment: LANDING_EXPERIMENT, audience, id,
    variant: random < 0.5 ? 'control' : 'account-first', assignedAt: now, seenAt: now };
}
